/**
 * Add from a machine: repositories each machine already registered (`viko setup` inside a repo,
 * `viko project add`) come from its end-to-end encrypted `machine.projects` report and add in one
 * click: the account project is created and that machine adopts its checkout with a device-signed
 * `project.adopt` (no passkey). Then "Also check out on <machine>?" (1 click, 1 passkey). A
 * repository not yet registered goes to that machine as a signed `project.register` command. Raw
 * configuration import stays behind an Advanced disclosure.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { parseProjectConfig } from '@viko/project-config'
import {
  type MachineCandidate,
  type MachineProject,
  type MachineProjectsFrame,
  type ProjectDraftSummary,
  ProjectRegisterResult,
} from '@viko/protocol'
import './dialogs.css'
import { Button, Sheet } from '@viko/transcript-ui'
import { AlertTriangle, ChevronRight, FileUp, FolderGit2, GitBranch, Laptop } from 'lucide-react'
import { useId, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { useMachines, useProjects } from '../lib/queries.js'
import type { Machine, Project } from '../lib/types.js'
import { MachineAccessNotice, useMachineAccess } from './MachineAccess.js'
import { AddMachineDialog } from './ProjectCheckouts.js'
import { errorText } from './ui.js'

const CONFIG_BYTES = 128_000

/** `/Users/me/dev/app` → `~/dev/app` (display only). */
export function shortPath(p: string): string {
  return p.replace(/^\/(?:Users|home)\/[^/]+(?=\/|$)/, '~').replace(/^[A-Z]:\\Users\\[^\\]+/i, '~')
}

function useMachineProjects(online: string[]) {
  const b = useBackend()
  // Under the machines key: a machine report update (or an access grant) invalidates both.
  return useQuery({
    queryKey: ['machines', 'projects'],
    queryFn: () => b.api.machineProjects(),
    // An online machine that has not reported yet (e.g. just granted the key): poll briefly.
    refetchInterval: (q) =>
      online.some((id) => !q.state.data?.some((r) => r.machineId === id)) ? 4_000 : false,
  })
}

export function AddProjectDialog({
  onClose,
  onSaved,
}: {
  onClose(): void
  onSaved(project: Project): Promise<void> | void
}) {
  const b = useBackend()
  const machines = useMachines()
  const projects = useProjects()
  const reports = useMachineProjects((machines.data ?? []).filter((m) => m.online).map((m) => m.id))
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  // account-projects: after an adopt, offer the other online machines ("Also check out on …?").
  const [adopted, setAdopted] = useState<{ project: Project; machineId: string }>()
  const [alsoOn, setAlsoOn] = useState<string>()

  /**
   * account-projects: the account project is created, then the machine that reported the repo
   * adopts its checkout (device-signed `project.adopt`; it binds only a folder registered there).
   * Older daemons without checkout support keep the v1 behaviour (entity only).
   */
  const create = async (
    key: string,
    yaml: string | undefined,
    adopt?: { machine: Machine; path: string; name: string; remote?: string },
  ) => {
    setError('')
    setBusy(key)
    try {
      const saved = yaml
        ? await b.api.createProject({ yaml })
        : await b.api.createAccountProject({
            name: adopt?.name ?? key,
            remote: adopt?.remote ?? null,
          })
      if (adopt) {
        await b.dispatch('project.adopt', adopt.machine.id, {
          projectId: saved.id,
          localPath: adopt.path,
        })
        await b.api.recordCheckouts(saved.id, { machineIds: [adopt.machine.id], via: 'adopt' })
      }
      await onSaved(saved)
      const others = (machines.data ?? []).filter((m) => m.online && m.id !== adopt?.machine.id)
      if (adopt && others.length) setAdopted({ project: saved, machineId: adopt.machine.id })
      else onClose()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(null)
    }
  }

  const list = machines.data ?? []
  const added = new Set(
    (projects.data ?? []).map((p) => p.config?.project.id).filter((x): x is string => !!x),
  )
  const byMachine = new Map((reports.data ?? []).map((r) => [r.machineId, r.report]))
  const loading = machines.isPending || reports.isPending

  if (adopted && alsoOn)
    return <AddMachineDialog project={adopted.project} preselect={alsoOn} onClose={onClose} />

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add from a machine"
      subtitle="Names, paths and configuration stay end-to-end encrypted."
      placement="center"
      size="md"
    >
      <div className="dlg-body gap-6">
        {adopted ? (
          <section className="card grid gap-2 p-4" aria-label="Also check out">
            <p className="m-0 text-[14px]">
              Added <strong>{adopted.project.name}</strong>. Also check it out on another machine?
            </p>
            <div className="flex flex-wrap gap-2">
              {(machines.data ?? [])
                .filter((m) => m.online && m.id !== adopted.machineId)
                .map((m) => (
                  <Button key={m.id} size="sm" variant="primary" onClick={() => setAlsoOn(m.id)}>
                    Check out on {m.name}
                  </Button>
                ))}
              <Button size="sm" variant="ghost" onClick={onClose}>
                Done
              </Button>
            </div>
          </section>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 rounded-lg bg-status-fail-soft px-3 py-2 text-[14px]">
            {error}
          </p>
        ) : null}
        <section className="grid gap-2" aria-labelledby="ap-machines">
          <h3 id="ap-machines" className="dlg-label">
            On your machines
          </h3>
          {loading ? (
            <p className="m-0 text-[14px] text-text-3" role="status">
              Looking for projects on your machines…
            </p>
          ) : list.length === 0 ? (
            <NoMachines />
          ) : (
            <>
              {reports.isError ? (
                <p className="m-0 text-[14px] text-status-fail">
                  Could not read your machines’ project lists: {errorText(reports.error)}
                </p>
              ) : null}
              {list.map((m) => (
                <MachineGroup
                  key={m.id}
                  machine={m}
                  report={byMachine.get(m.id)}
                  added={added}
                  busy={busy}
                  onAdd={(p) =>
                    p.yaml
                      ? void create(
                          `${m.id}:${p.localId}`,
                          p.yaml,
                          byMachine.get(m.id)?.candidates
                            ? { machine: m, path: p.repoPath, name: p.name }
                            : undefined,
                        )
                      : undefined
                  }
                  onAdopt={(c) =>
                    void create(`${m.id}:${c.path}`, undefined, {
                      machine: m,
                      path: c.path,
                      name: c.name,
                      ...(c.remote ? { remote: `https://${c.remote}` } : {}),
                    })
                  }
                />
              ))}
            </>
          )}
        </section>
        {list.length ? (
          <RegisterRepository
            machines={list}
            disabled={!!busy}
            onRegistered={(m, p) => {
              if (added.has(p.localId)) {
                setError(`${p.name} is already added. Open it from the project list.`)
                return
              }
              if (p.yaml) void create(`${m.id}:${p.localId}`, p.yaml)
              else
                setError(
                  `${p.name} is registered on ${m.name}, but its configuration is too large to send. Import it under Advanced.`,
                )
            }}
          />
        ) : null}
        <ImportFile busy={busy === 'file'} onImport={(yaml) => create('file', yaml)} />
      </div>
    </Sheet>
  )
}

function NoMachines() {
  return (
    <div className="card grid gap-2 p-4">
      <p className="m-0 text-[14px] font-medium">No machines connected yet</p>
      <p className="m-0 text-[14px] text-text-2">
        Viko runs your projects on your own computers. Set up viko on your Mac or PC, then the
        repositories you register there appear here.
      </p>
      <div>
        <Link to="/settings/enroll" className="vk-btn vk-btn--secondary vk-btn--sm">
          <span>Set up a machine</span>
        </Link>
      </div>
    </div>
  )
}

function MachineGroup({
  machine: m,
  report,
  added,
  busy,
  onAdd,
  onAdopt,
}: {
  machine: Machine
  report: MachineProjectsFrame | undefined
  added: Set<string>
  busy: string | null
  onAdd(p: MachineProject): void
  onAdopt(c: MachineCandidate): void
}) {
  // account-projects: a v2 daemon lists unbound repos as candidates; a v1 project already bound
  // to an account project is not a candidate any more.
  const unbound = report?.candidates ? new Set(report.candidates.map((c) => c.path)) : undefined
  const fresh = (report?.projects ?? []).filter(
    (p) => !added.has(p.localId) && (!unbound || unbound.has(p.repoPath)),
  )
  const bare = (report?.candidates ?? []).filter(
    (c) => !(report?.projects ?? []).some((p) => p.repoPath === c.path),
  )
  const already = (report?.projects.length ?? 0) - fresh.length
  const headId = useId()
  return (
    <article className="dlg-group" aria-labelledby={headId}>
      <header className="dlg-group__head">
        <Laptop size={16} aria-hidden="true" />
        <span id={headId} className="min-w-0 flex-1 truncate">
          {m.name}
        </span>
        {m.online ? (
          <span className="flex items-center gap-1.5 text-[14px] font-normal text-status-ok">
            <span className="h-2 w-2 rounded-full bg-status-ok" aria-hidden="true" />
            online
          </span>
        ) : (
          <span className="text-[14px] font-normal text-text-2">offline</span>
        )}
      </header>
      {!report ? (
        <NoReport machine={m} />
      ) : (
        <ul className="dlg-group__body m-0 list-none">
          {fresh.map((p) => {
            const key = `${m.id}:${p.localId}`
            const reason = !m.online
              ? `${m.name} is offline. Add unlocks when it reconnects.`
              : !p.yaml
                ? 'Its configuration is larger than 64 KB. Import it under Advanced.'
                : ''
            return (
              <li
                key={p.localId}
                className="flex items-center gap-3 border-b border-border-subtle py-2.5 last:border-b-0"
              >
                <FolderGit2 size={16} aria-hidden="true" className="flex-none text-text-2" />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate text-[14px] font-medium">{p.name}</span>
                  <span className="flex min-w-0 items-center gap-2 text-[14px] text-text-3">
                    <span className="truncate" title={p.repoPath}>
                      {shortPath(p.repoPath)}
                    </span>
                    <span className="flex flex-none items-center gap-1">
                      <GitBranch size={12} aria-hidden="true" />
                      {p.defaultBranch}
                    </span>
                    {p.lanes.length ? (
                      <span className="flex-none">
                        {p.lanes.length} release lane{p.lanes.length === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </span>
                  {reason ? <span className="text-[14px] text-text-3">{reason}</span> : null}
                </span>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!!busy || !!reason}
                  aria-label={`Add ${p.name}`}
                  title={reason || undefined}
                  onClick={() => onAdd(p)}
                >
                  {busy === key ? 'Adding…' : 'Add'}
                </Button>
              </li>
            )
          })}
          {bare.map((c) => (
            <li
              key={c.path}
              className="flex items-center gap-3 border-b border-border-subtle py-2.5 last:border-b-0"
            >
              <FolderGit2 size={16} aria-hidden="true" className="flex-none text-text-2" />
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span className="truncate text-[14px] font-medium">
                  {c.offered ? `Found ${c.name}` : c.name}
                </span>
                <span className="truncate text-[14px] text-text-3" title={c.path}>
                  {shortPath(c.path)}
                  {c.remote ? ` · ${c.remote}` : ''}
                </span>
              </span>
              <Button
                size="sm"
                variant="primary"
                disabled={!!busy || !m.online}
                aria-label={`Add ${c.name}`}
                title={
                  m.online ? undefined : `${m.name} is offline. Add unlocks when it reconnects.`
                }
                onClick={() => onAdopt(c)}
              >
                {busy === `${m.id}:${c.path}` ? 'Adding…' : 'Add'}
              </Button>
            </li>
          ))}
          {report.problems
            .filter((p) => !bare.some((c) => c.path === p.repoPath))
            .map((p) => (
              <li
                key={p.repoPath}
                className="flex items-start gap-3 border-b border-border-subtle py-2.5 last:border-b-0"
              >
                <AlertTriangle
                  size={16}
                  aria-hidden="true"
                  className="mt-0.5 flex-none text-status-wait"
                />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate text-[14px] font-medium">{p.repoName}</span>
                  <span className="text-[14px] text-text-3">
                    Its viko.project.yaml needs fixing before it can be added: {p.error}
                  </span>
                </span>
              </li>
            ))}
          {!fresh.length && !bare.length && !report.problems.length ? (
            <li className="py-2 text-[14px] text-text-2">
              {already
                ? `Every project on ${m.name} is already added.`
                : `No repositories registered on ${m.name} yet. Add one below.`}
            </li>
          ) : already ? (
            <li className="py-2 text-[14px] text-text-2">{already} already added</li>
          ) : null}
        </ul>
      )}
    </article>
  )
}

/** No project list yet: usually the machine lacks the account key, which only a grant fixes. */
function NoReport({ machine: m }: { machine: Machine }) {
  const b = useBackend()
  const access = useMachineAccess(m.id, m.online)
  const blocked = !!b.content && access.data !== undefined && access.data !== 'granted'
  if (blocked && access.data !== 'locked')
    return (
      <div className="dlg-group__body">
        <MachineAccessNotice machine={m} />
      </div>
    )
  return (
    <p className="dlg-group__body m-0" role="status">
      {!m.online
        ? 'No project list from this machine yet. It appears once the machine reconnects.'
        : b.content && access.isPending
          ? 'Checking this machine’s access…'
          : `Waiting for ${m.name} to share its project list. You can also add a repository below.`}
    </p>
  )
}

function RegisterRepository({
  machines,
  disabled,
  onRegistered,
}: {
  machines: Machine[]
  disabled: boolean
  onRegistered(m: Machine, p: MachineProject): void
}) {
  const b = useBackend()
  const online = machines.filter((m) => m.online)
  const [machineId, setMachineId] = useState(online[0]?.id ?? '')
  const [path, setPath] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<ProjectDraftSummary | null>(null)
  const machine = machines.find((m) => m.id === machineId)
  const pathId = useId()

  const send = async (confirm?: ProjectDraftSummary) => {
    if (!machine) return
    setError('')
    setBusy(true)
    try {
      const out = await b.dispatch('project.register', machine.id, {
        repoPath: confirm?.repoPath ?? path.trim(),
        ...(confirm ? { writeConfig: true, draftDigest: confirm.digest } : {}),
      })
      const result = ProjectRegisterResult.parse(out.result)
      if (result.status === 'needs-config') {
        if (confirm && result.draft.digest !== confirm.digest)
          setError('The repository changed since the draft. Review the updated draft.')
        setDraft(result.draft)
        return
      }
      setDraft(null)
      setPath('')
      onRegistered(machine, result.project)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="grid gap-3" aria-labelledby="ap-register">
      <div className="grid gap-1">
        <h3 id="ap-register" className="dlg-label">
          Add a repository from a machine
        </h3>
        <p className="m-0 text-[14px] text-text-3">
          For a repository that isn’t listed above. Viko registers it on that machine.
        </p>
      </div>
      {online.length === 0 ? (
        <p className="m-0 text-[14px] text-text-3">
          All your machines are offline. Bring one online to add a repository from it.
        </p>
      ) : draft ? (
        <DraftReview
          draft={draft}
          machine={machine}
          busy={busy}
          onConfirm={() => void send(draft)}
          onCancel={() => {
            setDraft(null)
            setError('')
          }}
        />
      ) : (
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void send()
          }}
        >
          {machines.length > 1 ? (
            <div className="choice" role="radiogroup" aria-label="Machine">
              {machines.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={m.id === machineId}
                  disabled={!m.online || busy}
                  title={m.online ? undefined : `${m.name} is offline`}
                  onClick={() => setMachineId(m.id)}
                >
                  <Laptop size={14} aria-hidden="true" />
                  {m.name}
                </button>
              ))}
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <label htmlFor={pathId} className="text-[14px] font-medium text-text-2">
              Folder on {machine?.name ?? 'the machine'}
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                id={pathId}
                className="input min-w-[200px] flex-1"
                value={path}
                disabled={busy || disabled}
                placeholder="~/dev/my-app"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setPath(e.target.value)}
              />
              <Button
                type="submit"
                variant="secondary"
                disabled={busy || disabled || !path.trim() || !machine}
              >
                {busy ? 'Checking…' : 'Add repository'}
              </Button>
            </div>
          </div>
        </form>
      )}
      {error ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          {error}
        </p>
      ) : null}
    </section>
  )
}

function DraftReview({
  draft,
  machine,
  busy,
  onConfirm,
  onCancel,
}: {
  draft: ProjectDraftSummary
  machine: Machine | undefined
  busy: boolean
  onConfirm(): void
  onCancel(): void
}) {
  const rows: [string, string][] = [
    ['Name', draft.name],
    ['Branch', draft.defaultBranch],
    ['Test', draft.test.join('\n') || 'None found'],
    ['Build', draft.build.join('\n') || 'None found'],
    [
      'Release lanes',
      draft.lanes.map((l) => `${l.id} (${l.target})`).join('\n') || 'None yet; add them later',
    ],
  ]
  return (
    <section className="card grid gap-3 p-4" aria-label="Review configuration">
      <p className="m-0 text-[14px] text-text-2">
        <strong className="font-semibold text-text-1">{shortPath(draft.repoPath)}</strong> has no
        viko.project.yaml yet. Viko read the repository and drafted one:
      </p>
      <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-[14px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-text-3">{k}</dt>
            <dd className="m-0 whitespace-pre-wrap break-words">{v}</dd>
          </div>
        ))}
      </dl>
      {draft.questions.length || draft.assumptions.length ? (
        <ul className="m-0 grid gap-1 pl-4 text-[14px] text-text-3">
          {[...draft.questions, ...draft.assumptions].slice(0, 6).map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      ) : null}
      <p className="m-0 text-[14px] text-text-3">
        Confirming writes viko.project.yaml into the repository on {machine?.name ?? 'the machine'}.
        You can edit or commit it later.
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" disabled={busy} onClick={onConfirm}>
          {busy ? 'Writing…' : 'Write file and add project'}
        </Button>
      </div>
    </section>
  )
}

function readText(f: File): Promise<string> {
  if (typeof f.text === 'function') return f.text()
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result ?? ''))
    r.onerror = () => reject(r.error)
    r.readAsText(f)
  })
}

function ImportFile({ busy, onImport }: { busy: boolean; onImport(yaml: string): void }) {
  const [file, setFile] = useState<{ name: string; yaml: string } | null>(null)
  const [error, setError] = useState('')
  const inputId = useId()
  const choose = async (f: File | undefined) => {
    setError('')
    setFile(null)
    if (!f) return
    if (f.size > CONFIG_BYTES) return setError('Configuration must be smaller than 128 KB.')
    let yaml: string
    try {
      yaml = await readText(f)
    } catch {
      return setError('Could not read this file.')
    }
    const parsed = parseProjectConfig(yaml)
    if (!parsed.ok)
      return setError(
        parsed.errors.map((e) => `${e.path || 'Configuration'}: ${e.message}`).join('\n'),
      )
    setFile({ name: f.name, yaml })
  }
  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[14px] text-text-2 [&::-webkit-details-marker]:hidden">
        <ChevronRight
          size={14}
          aria-hidden="true"
          className="transition-transform group-open:rotate-90"
        />
        Advanced: import configuration file
      </summary>
      <div className="grid gap-3 pt-3 pl-5">
        <p className="m-0 text-[14px] text-text-3">
          Import a viko.project.yaml directly. The repository must also be added on its machine with{' '}
          <code>viko project add</code> before Viko can run it.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={inputId} className="vk-btn vk-btn--secondary vk-btn--sm cursor-pointer">
            <FileUp size={14} aria-hidden="true" />
            <span>Choose file</span>
          </label>
          <input
            id={inputId}
            type="file"
            accept=".yaml,.yml"
            className="sr-only"
            aria-label="Configuration file"
            disabled={busy}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <span className="min-w-0 truncate text-[14px] text-text-3">
            {file?.name ?? 'No file chosen'}
          </span>
          <Button
            size="sm"
            variant="primary"
            className="ml-auto"
            disabled={!file || busy}
            onClick={() => file && onImport(file.yaml)}
          >
            {busy ? 'Importing…' : 'Import project'}
          </Button>
        </div>
        {error ? (
          <p role="alert" className="m-0 whitespace-pre-wrap text-[14px] text-status-fail">
            {error}
          </p>
        ) : null}
      </div>
    </details>
  )
}
